/**
 * <mb-recently-viewed-rail> — PDP "Recently viewed" grid.
 * ─────────────────────────────────────────────────────────────────────────────
 * Same architecture as <modern-product-recommendations> (assets/
 * modern-product.js): the card markup is server-rendered by the Section
 * Rendering API and swapped in — no HTML is built in JS.
 *
 * Flow:
 *   1. Read ids from the window.MBRecentlyViewed localStorage store
 *      (written by <mb-recently-viewed-tracker> on every PDP visit),
 *      excluding the product being viewed, newest first, capped at
 *      data-limit.
 *   2. Fetch {data-search-url}?type=product&q=id:1 OR id:2…
 *      &section_id={data-section-type}. The section renders the results
 *      as modern-collection-card markup inside [data-mb-rv-payload]
 *      (see sections/modern-recently-viewed.liquid).
 *   3. Reorder the returned cards to recency (search ranks by relevance),
 *      swap them into [data-mb-rv-slot], and unhide the host.
 *
 * The host ships `hidden`. Empty store, fetch failure, or zero results all
 * leave it hidden — no empty band, no layout shift for first-time visitors.
 */

if (!customElements.get('mb-recently-viewed-rail')) {
  class MBRecentlyViewedRail extends HTMLElement {
    #abort = null;

    connectedCallback() {
      const slot = this.querySelector('[data-mb-rv-slot]');
      if (!slot || !window.MBRecentlyViewed) return;

      const exclude = this.dataset.exclude ?? '';
      const limit = Number(this.dataset.limit) || 4;
      const ids = (window.MBRecentlyViewed.get() ?? [])
        .filter((p) => p && p.id != null && String(p.id) !== exclude)
        .slice(0, limit)
        .map((p) => String(p.id));
      if (!ids.length) return;

      this.#fetchCards(slot, ids);
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    async #fetchCards(slot, ids) {
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url =
        `${this.dataset.searchUrl || '/search'}` +
        `?type=product&q=${encodeURIComponent(query)}` +
        `&section_id=${this.dataset.sectionType || 'modern-recently-viewed'}`;

      this.#abort = new AbortController();
      try {
        const res = await fetch(url, { signal: this.#abort.signal });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const payload = doc.querySelector('[data-mb-rv-payload]');
        if (!payload || payload.children.length === 0) return;

        this.#restoreRecencyOrder(payload, ids);
        slot.replaceChildren(payload);
        this.hidden = false;
      } catch {
        /* network error or abort — the section simply stays hidden */
      }
    }

    /** Search ranks by relevance; put cards back in the order we asked for. */
    #restoreRecencyOrder(list, ids) {
      const rank = new Map(ids.map((id, i) => [id, i]));
      [...list.children]
        .sort(
          (a, b) =>
            (rank.get(a.dataset.mbRvId) ?? ids.length) -
            (rank.get(b.dataset.mbRvId) ?? ids.length)
        )
        .forEach((item) => list.appendChild(item));
    }
  }

  customElements.define('mb-recently-viewed-rail', MBRecentlyViewedRail);
}
